/**
 * PlatformOrgs — the platform-admin view across ALL organizations: a
 * business summary and a sortable, filterable org list. Only platform
 * admins reach it (the backend returns 404 for everyone else).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  Flex,
  Grid,
  Heading,
  Link,
  Select,
  Spinner,
  Table,
  Text,
  TextField,
} from '@radix-ui/themes';
import {
  fetchPlatformOrgs,
  filterOrgs,
  formatDate,
  sortOrgs,
  type OrgFilter,
  type OrgSortKey,
  type PlatformOrgList,
  type PlatformOrgRow,
} from '../../utils/platform';

const STATUS_COLORS: Record<string, 'green' | 'blue' | 'red' | 'gray' | 'amber'> = {
  active: 'green',
  comped: 'green',
  trialing: 'blue',
  past_due: 'red',
  suspended: 'red',
  canceled: 'gray',
  trial_expired: 'amber',
  comp_expired: 'amber',
};

export function StatusBadge({ status }: { status: string | null }) {
  const s = status ?? 'none';
  return <Badge color={STATUS_COLORS[s] ?? 'gray'} variant="soft">{s.replace('_', ' ')}</Badge>;
}

function usageText(used: number | null, limit: number | null | undefined): string {
  if (used === null) return 'n/a';
  return limit === null || limit === undefined ? `${used}` : `${used} / ${limit}`;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <Text as="div" size="1" color="gray">{label}</Text>
      <Text as="div" size="6" weight="bold">{value}</Text>
    </Card>
  );
}

const COLUMNS: { key: OrgSortKey | null; label: string }[] = [
  { key: 'name', label: 'Organization' },
  { key: 'plan', label: 'Plan' },
  { key: 'trialEndsAt', label: 'Trial ends' },
  { key: 'venues', label: 'Venues' },
  { key: null, label: 'Nights this month' },
  { key: null, label: 'AI credits this month' },
  { key: 'lastGameNightAt', label: 'Last night' },
  { key: 'lastActivityAt', label: 'Last activity' },
  { key: 'createdAt', label: 'Created' },
];

const PlatformOrgs: React.FC = () => {
  const [data, setData] = useState<PlatformOrgList | null>(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<OrgFilter>({ q: '', plan: '', status: '', trialSoon: false });
  const [sort, setSort] = useState<{ key: OrgSortKey; dir: 'asc' | 'desc' }>({ key: 'createdAt', dir: 'desc' });

  useEffect(() => {
    fetchPlatformOrgs().then(setData).catch(() => setError(true));
  }, []);

  const rows = useMemo(
    () => (data ? sortOrgs(filterOrgs(data.orgs, filter), sort.key, sort.dir) : []),
    [data, filter, sort],
  );

  if (error) {
    return <Callout.Root color="red"><Callout.Text>Could not load organizations.</Callout.Text></Callout.Root>;
  }
  if (!data) {
    return <Flex align="center" gap="2"><Spinner /><Text color="gray">Loading organizations…</Text></Flex>;
  }

  const { summary } = data;
  const plans = Object.keys(summary.byPlan).sort();
  const statuses = Object.keys(summary.byStatus).sort();
  const toggleSort = (key: OrgSortKey) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  return (
    <Flex direction="column" gap="4">
      <Box>
        <Heading size="6">Organizations</Heading>
        <Text size="2" color="gray">Every organization on the platform</Text>
      </Box>

      {!data.billingEnabled && (
        <Callout.Root color="gray">
          <Callout.Text>
            Billing is off, so every org is unlimited now. Plans and statuses show what applies once billing is on.
          </Callout.Text>
        </Callout.Root>
      )}

      <Grid columns={{ initial: '2', md: '4' }} gap="3">
        <Stat label="Organizations" value={summary.orgCount} />
        <Stat label="Estimated MRR" value={`$${summary.estimatedMrrUsd.toLocaleString()}`} />
        <Stat label="New this month" value={summary.newOrgsThisMonth} />
        <Stat label="Trials ending in 7 days" value={summary.trialsEndingSoon.length} />
      </Grid>

      <Card>
        <Flex direction="column" gap="2">
          <Flex gap="2" wrap="wrap" align="center">
            <Text size="2" color="gray">By plan:</Text>
            {plans.map((p) => <Badge key={p} variant="outline">{p}: {summary.byPlan[p]}</Badge>)}
          </Flex>
          <Flex gap="2" wrap="wrap" align="center">
            <Text size="2" color="gray">By status:</Text>
            {statuses.map((s) => <Badge key={s} color={STATUS_COLORS[s] ?? 'gray'} variant="soft">{s}: {summary.byStatus[s]}</Badge>)}
          </Flex>
          {summary.trialsEndingSoon.length > 0 && (
            <Flex gap="2" wrap="wrap" align="center">
              <Text size="2" color="gray">Trials ending soon:</Text>
              {summary.trialsEndingSoon.map((t) => (
                <Link key={t.id} asChild size="2">
                  <RouterLink to={`/platform/orgs/${t.id}`}>{t.name} ({t.trialDaysLeft}d)</RouterLink>
                </Link>
              ))}
            </Flex>
          )}
        </Flex>
      </Card>

      <Flex gap="3" wrap="wrap" align="center">
        <Box style={{ minWidth: 220 }}>
          <TextField.Root
            placeholder="Search name, slug, owner"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            aria-label="Search organizations"
          />
        </Box>
        <Select.Root value={filter.plan || 'any'} onValueChange={(v) => setFilter((f) => ({ ...f, plan: v === 'any' ? '' : v }))}>
          <Select.Trigger aria-label="Filter by plan" />
          <Select.Content>
            <Select.Item value="any">Any plan</Select.Item>
            {plans.map((p) => <Select.Item key={p} value={p}>{p}</Select.Item>)}
          </Select.Content>
        </Select.Root>
        <Select.Root value={filter.status || 'any'} onValueChange={(v) => setFilter((f) => ({ ...f, status: v === 'any' ? '' : v }))}>
          <Select.Trigger aria-label="Filter by status" />
          <Select.Content>
            <Select.Item value="any">Any status</Select.Item>
            {statuses.map((s) => <Select.Item key={s} value={s}>{s}</Select.Item>)}
          </Select.Content>
        </Select.Root>
        <Text as="label" size="2">
          <Flex gap="2" align="center">
            <Checkbox
              checked={filter.trialSoon}
              onCheckedChange={(c) => setFilter((f) => ({ ...f, trialSoon: c === true }))}
            />
            Trial ends in 7 days
          </Flex>
        </Text>
      </Flex>

      <Box style={{ overflowX: 'auto' }}>
        <Table.Root variant="surface" size="1">
          <Table.Header>
            <Table.Row>
              {COLUMNS.map((c) => (
                <Table.ColumnHeaderCell key={c.label}>
                  {c.key ? (
                    <Button variant="ghost" size="1" color="gray" highContrast onClick={() => toggleSort(c.key as OrgSortKey)}>
                      {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </Button>
                  ) : c.label}
                </Table.ColumnHeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((r: PlatformOrgRow) => (
              <Table.Row key={r.id}>
                <Table.RowHeaderCell>
                  <Flex direction="column">
                    <Link asChild weight="medium">
                      <RouterLink to={`/platform/orgs/${r.id}`}>{r.name}</RouterLink>
                    </Link>
                    <Text size="1" color="gray">{r.slug}{r.ownerEmail ? ` · ${r.ownerEmail}` : ''}</Text>
                  </Flex>
                </Table.RowHeaderCell>
                <Table.Cell>
                  <Flex gap="1" wrap="wrap">
                    <Badge variant="outline">{r.effectivePlan}</Badge>
                    <StatusBadge status={r.suspendedAt ? 'suspended' : r.effectiveStatus} />
                    {r.hasOverrides && <Badge color="purple" variant="soft">overrides</Badge>}
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  {r.trialEndsAt ? `${formatDate(r.trialEndsAt)} (${r.trialDaysLeft}d)` : '—'}
                </Table.Cell>
                <Table.Cell>{usageText(r.usage.venues, r.limits.venues)}</Table.Cell>
                <Table.Cell>{usageText(r.usage.gameNightsPerMonth, r.limits.gameNightsPerMonth)}</Table.Cell>
                <Table.Cell>{usageText(r.usage.aiCreditsPerMonth, r.limits.aiCreditsPerMonth)}</Table.Cell>
                <Table.Cell>{formatDate(r.lastGameNightAt)}</Table.Cell>
                <Table.Cell>{formatDate(r.lastActivityAt)}</Table.Cell>
                <Table.Cell>{formatDate(r.createdAt)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
      {rows.length === 0 && <Text color="gray">No organizations match these filters.</Text>}
    </Flex>
  );
};

export default PlatformOrgs;
